/*
 * Copyright Red Hat, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createApiRef, type ApiRef } from '@backstage/core-plugin-api';

import {
  Attachment,
  BaseMessage,
  CaptureFeedback,
  ConversationList,
  LCSModel,
} from '../types';

/**
 * @public
 * Lightspeed API
 */

export type LightspeedAPI = {
  getAllModels: () => Promise<LCSModel[]>;
  getConversationMessages: (conversation_id: string) => Promise<BaseMessage[]>;
  createMessage: (
    prompt: string,
    selectedModel: string,
    selectedProvider: string,
    conversation_id: string,
    attachments: Attachment[],
  ) => Promise<ReadableStreamDefaultReader>;
  deleteConversation: (
    conversation_id: string,
  ) => Promise<{ success: boolean }>;
  renameConversation: (
    conversation_id: string,
    newName: string,
  ) => Promise<{ success: boolean }>;
  getConversations: () => Promise<ConversationList>;
  getNotebookConversationIds: () => Promise<string[]>;
  getFeedbackStatus: () => Promise<boolean>;
  captureFeedback: (payload: CaptureFeedback) => Promise<{ response: string }>;
  isTopicRestrictionEnabled: () => Promise<boolean>;
  stopMessage: (requestId: string) => Promise<{ success: boolean }>;
};

/**
 * @public
 * Lightspeed API interface
 */

export const lightspeedApiRef: ApiRef<LightspeedAPI> =
  createApiRef<LightspeedAPI>({
    id: 'plugin.lightspeed.service',
  });
